export const DEVICE_SIZES = [640, 750, 828, 1080, 1200, 1400]

/**
 * Candidate widths for the item-page hero — the LCP element.
 *
 * Deliberately narrower than DEVICE_SIZES so the set is small enough to warm
 * exhaustively (see services.image_cdn.warm_widths, which MUST match). Every
 * realistic device lands on one of these with <=15% linear over-fetch:
 *
 *   Lighthouse mobile (412px @ DPR 1.75) -> 721 -> 828
 *   mobile DPR 2 (390px)                 -> 780 -> 828
 *   mobile DPR 3 (390px)                 -> 1170 -> 1200
 *   desktop DPR 1 (950px hero)           -> 950 -> 1080
 *   desktop DPR 2                        -> 1900 -> 1400 (clamped)
 *
 * 640 and 750 are excluded on purpose: including 750 previously caused the
 * preload and the <img> to select different candidates on DPR-1.75 devices,
 * downloading the LCP image twice.
 *
 * Both the hero <img> and <HeroPreload> must be given this exact list. If they
 * ever diverge the browser preloads one variant and then fetches another.
 */
export const HERO_WIDTHS = [828, 1080, 1200, 1400]

/**
 * `sizes` for the item-page hero, shared by BOTH galleries and the preload.
 *
 * MobileGallery and PhotoGrid are both always in the DOM (each hidden at the
 * other's breakpoint via CSS) and both heroes are eager, so `loading="eager"`
 * fetches them regardless of `display:none`. Giving them different `sizes`
 * therefore downloaded two different hero variants on every page view — 1080 +
 * 1400 on desktop, 750 + 828 on mobile.
 *
 * Handing both the same string makes them resolve to the same candidate at any
 * viewport, so the hidden one dedupes onto the visible one's request instead of
 * costing an extra full-size image and a cold transform. The value tracks the
 * *visible* element at each breakpoint: 950px for the desktop grid hero, 100vw
 * for the mobile hero.
 */
export const HERO_SIZES = "(min-width:1024px) 950px, 100vw"

/**
 * `sizes` for the homepage hero carousel (TECH-1138), shared by its <img> and
 * its <HeroPreload>. Paired with HERO_WIDTHS, which are warmed.
 *
 * The photo fills the content column below 976px (100vw less 20px either
 * side) and the auction column of the 65/35 split from 976px: 65% of the
 * content width less the 20px gutter at the boundary, which tops out at 844px
 * once the column caps at 1440px with 40px padding.
 */
export const HOME_HERO_SIZES =
    "(min-width: 1440px) 844px, (min-width: 976px) calc(65vw - 46px), calc(100vw - 40px)"

/**
 * Candidate widths and `sizes` for listing cards (FirearmCard).
 *
 * Cards previously advertised no `sizes` at all, so the browser fell back to the
 * spec default of `100vw` and, on a 412px viewport at DPR 1.75, selected 750w —
 * a width nothing warms. Every card on every collection page therefore paid a
 * cold transform.
 *
 * The grid is `grid-cols-1 lg:grid-cols-2 xl:grid-cols-3`, so:
 *
 *   mobile (1 col, 100vw)  412 @ DPR 1.75 -> 721  -> 828   (warmed)
 *   mobile               390 @ DPR 3      -> 1170 -> 1200  (warmed)
 *   lg (2 col, 50vw)     1024 -> 512      -> 640
 *   xl (3 col, 33vw)     1280 -> 422      -> 640
 *   xl @ DPR 2           -> 844           -> 1200  (warmed)
 *
 * 640 is included for desktop so mobile cards are not forced onto an 828 they
 * do not need. It is warmed today (services.image_cdn.warm_widths includes it),
 * though the contract only requires the mobile-selectable widths to be warmed —
 * ImageCdnContractTest asserts that relationship, not full coverage.
 */
export const CARD_WIDTHS = [640, 828, 1200]

/**
 * Width for absolute metadata image URLs (og:image, twitter:image).
 *
 * MUST match services.image_cdn.metadata_width, which the server uses for
 * schema.org `image`, so a page's Open Graph and structured-data images resolve
 * to the same cache object instead of generating two variants.
 *
 * 1200 is the Open Graph recommendation. Link unfurlers fetch this URL directly
 * — they do not evaluate srcset — so before this existed they downloaded the
 * unresized 200-490 KiB origin JPEG.
 */
export const METADATA_WIDTH = 1200

export const CARD_SIZES =
    "(min-width: 1280px) 33vw, (min-width: 1024px) 50vw, 100vw"

/**
 * Candidate widths for the item-page gallery thumbnails, which render at 220px
 * on desktop. Intentionally not warmed: they are never the LCP element, and at
 * these sizes a transform costs roughly (440/1200)^2 of the hero's, i.e. well
 * under a second, absorbed concurrently on first view.
 */
export const THUMBNAIL_WIDTHS = [220, 440, 660]

// Only the S3 host is rewritten. The optimization Lambda reads originals with
// s3:GetObject and cannot fetch over HTTP, so a /img/gcs/... URL would resolve
// to a nonexistent S3 key and 404. Photos on any other object-storage host are
// unservable (see RAW_BUCKET_HOST_PATTERNS) and never rendered; the server
// already drops them from photo_array, and surviving legacy GCS objects are
// copied into the S3 bucket by images:migrate-gcs.
const REWRITE_HOSTS = {
    "cash-for-arms-pictures.s3.amazonaws.com": "s3",
}

// Hostname patterns of raw object-storage origins the pipeline cannot serve. A
// URL on one of these hosts (that is not in REWRITE_HOSTS) is treated as
// missing: buildImageUrl returns "" so Image falls back to its placeholder
// instead of emitting an unresized original — or a 404 — into the DOM. This is
// the client-side half of the guard: it holds even when a payload the server
// did not filter reaches the page (an Echo event from an older build, say).
// Mirrors ImageCdnService::RAW_BUCKET_HOST_PATTERNS; ImageCdnContractTest
// asserts the two lists are identical.
const RAW_BUCKET_HOST_PATTERNS = [
    "storage\\.googleapis\\.com",
    "[^.]+\\.storage\\.googleapis\\.com",
    "s3\\.amazonaws\\.com",
    "s3\\.[a-z0-9-]+\\.amazonaws\\.com",
    "[^.]+\\.s3\\.amazonaws\\.com",
    "[^.]+\\.s3\\.[a-z0-9-]+\\.amazonaws\\.com",
]

const RAW_BUCKET_HOST = new RegExp(
    `^(?:${RAW_BUCKET_HOST_PATTERNS.join("|")})$`,
    "i"
)

const stripQuotes = (s) => (typeof s === "string" ? s.replace(/"/g, "") : "")

const isBypass = (u) => /\.(gif|svg)(\?|#|$)/i.test(u)

const CDN_BASE = import.meta.env.VITE_IMAGE_CDN_BASE || ""

const hostOf = (u) => {
    try {
        return new URL(u).hostname
    } catch {
        return ""
    }
}

export const isRawBucketHost = (rawUrl) => {
    const host = hostOf(stripQuotes(rawUrl))

    return host !== "" && !REWRITE_HOSTS[host] && RAW_BUCKET_HOST.test(host)
}

// `format=auto` is required for AVIF/WebP negotiation: the edge function resolves
// it against the request's Accept header and bakes the concrete format into the
// cache key. Omit it and the Lambda skips toFormat() entirely, returning the
// original content-type — resized, but still JPEG.
export const buildImageUrl = (
    rawUrl,
    { width, quality = 70, format = "auto" } = {}
) => {
    const url = stripQuotes(rawUrl)
    if (!url) return url
    // Never emit a raw bucket URL, whatever its extension: the caller's
    // fallback (a placeholder) is the correct rendering of an unservable photo.
    if (isRawBucketHost(url)) return ""
    if (isBypass(url)) return url
    const source = REWRITE_HOSTS[hostOf(url)]
    if (!source) return url
    const { pathname } = new URL(url)
    const qs = new URLSearchParams()
    if (format) qs.set("format", format)
    if (width) qs.set("width", String(width))
    if (quality) qs.set("quality", String(quality))
    const query = qs.toString()
    return `${CDN_BASE}/img/${source}${pathname}${query ? `?${query}` : ""}`
}

export const buildSrcSet = (rawUrl, widths = DEVICE_SIZES, quality = 70) => {
    const url = stripQuotes(rawUrl)
    if (!url || isBypass(url) || !REWRITE_HOSTS[hostOf(url)]) return undefined
    return widths
        .map((w) => `${buildImageUrl(url, { width: w, quality })} ${w}w`)
        .join(", ")
}

/**
 * Candidate widths and `sizes` for the mobile listing-page thumbnail strip
 * (FAL-243), which renders four thumbs across the viewport at roughly 23vw.
 *
 * These are deliberately their own set rather than THUMBNAIL_WIDTHS: the strip
 * ships in the server-rendered HTML alongside the hero, so anything wider than
 * the strip actually paints is bytes competing with the LCP element. 23vw on a
 * 430px viewport at DPR 3 needs 297px, so 360 is the top candidate.
 *
 * They must also stay visibly smaller than the hero's renditions. FAL-199 cut
 * the initial HTML down to one gun photo so Google had a single unambiguous
 * SERP-thumbnail candidate; these four are back in the document, and what keeps
 * them from being four more equally-plausible candidates is that they are
 * 120-360px renditions of a photo that is not the declared
 * `primaryImageOfPage`, well under the size Google will promote.
 */
export const MOBILE_THUMB_WIDTHS = [120, 240, 360]

export const MOBILE_THUMB_SIZES = "23vw"

/**
 * `sizes` for a photo inside either viewer. Both are full-window overlays, so
 * the image is laid out against the whole viewport at every breakpoint.
 *
 * Paired with HERO_WIDTHS on purpose: the viewer's opening photo is usually the
 * hero, and advertising the hero's exact candidate list means opening the viewer
 * on photo 1 re-uses the rendition already in the browser cache instead of
 * fetching a second variant of the same JPEG.
 */
export const VIEWER_SIZES = "100vw"
